import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

// ASSETS
import RedLoop from "assets/svg/SearchIconRed.svg";

// STYLES
import { 
  SearchContainer, 
  SearchInput, 
  ResultsGrid, 
  NoResults, 
  SearchHeader, 
  SearchForm,
  SearchButton,
  InputContainer,
  ImgStyled
} from './Search.styled';

// LIBRARIES
import { useTranslation } from "react-i18next";
// MISC
import { searchInMockData } from 'constants/mock/searchMockData';

// COMPONENTS
import SearchCard from 'components/Molecules/SearchCard/SearchCard';
import { SearchResult } from 'components/Molecules/SearchCard/SearchCardModel';
import { COLORS } from 'constants/variables';
import CustomText from 'components/Atoms/CustomText/CustomText';
import { TextType } from 'components/Atoms/CustomText/CustomTextModel';
import InputAtom from "components/Atoms/InputAtom/InputAtom";
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import { ButtonType } from 'components/Atoms/ButtonAtom/ButtonAtomModel';
import { OptionType } from 'components/Atoms/InputAtom/InputAtomModel';

const Search = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') ?? '';
  const [query, setQuery] = useState(initialQ);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const q = searchParams.get('q') ?? '';
    setQuery(q);
    if (q.trim()) {
      performSearch(q);
    } else {
      setResults([]);
    }
  }, [searchParams]);

  const performSearch = async (q: string) => {
    setIsLoading(true);
    try {
      const res = await searchInMockData(q);
      setResults(res);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);

    // Optional: live-search as you type
    if (q.trim()) {
      performSearch(q);
    } else {
      setResults([]);
    }
  };

  return (
    <SearchContainer>
      <SearchHeader>
        <CustomText 
          type={TextType.H2}
          color={COLORS.RaisinBlack}
          text="Căutare"
        />
        <InputContainer>
          <form onSubmit={handleSearch}>
          <InputAtom
            startAdornment={<ImgStyled src={RedLoop} alt="Search" />}
            placeholder={t('components.search.searchPlaceholder')}
            endAdornment={
              <ButtonAtom
                type={ButtonType.ArrowDownDefault}
                text={t("view.newsAndMedia.officialAnnouncements.search")}
              />
            }
            isSearchBar
            value={query}
            onChange={handleChange}
            isModal
          />
          </form>
        </InputContainer>
      </SearchHeader>

      {isLoading ? (
        <div>{t('common.loading')}</div>
      ) : results.length > 0 ? (
        <ResultsGrid>
          {results.map(r => <SearchCard key={r.id} result={r} />)}
        </ResultsGrid>
      ) : query ? (
        <NoResults>
          <CustomText 
            type={TextType.P1}
            color={COLORS.RaisinBlack}
            text={t('components.search.noResults', { query })}
          />
        </NoResults>
      ) : (
        <NoResults>
          <CustomText 
            type={TextType.P1}
            color={COLORS.RaisinBlack}
            text={t('search.enterSearchTerm')}
          />
        </NoResults>
      )}
    </SearchContainer>
  );
};

export default Search;